import React, { useState, useEffect, useMemo, useCallback, Fragment, FC } from 'react'
import { Logger } from 'typescript-log'
import useWebSocket from 'react-use-websocket'
import { Block } from 'common/client-data-types/blocks'
import {
    StyledLiveBlogPost,
    StyledLiveBlogTimeline,
    StyledLiveBlogTimestamp,
    StyledLiveBlogAuthor,
    StyledArticleBlockContent,
    StyledQuestionForm,
    StyledQuestionInput,
    StyledQuestionSubmit,
    StyledConnectionStatus,
    StyledLoadMoreButton,
    StyledIconSpinner,
    StyledLoadMoreContainer,
} from './LiveBlog.styled'
import { PlayerEvent } from 'common/events/video-event-types'
import { LinkClickedEvent } from 'common/events/component-events'
import { useInterval } from 'common/hooks/useInterval'
import { colors } from 'component-library/__styling/settings'

export function isOurLiveBlog(url: string) {
    return url.indexOf('liveblog:') === 0
}

export interface LiveBlogPostResponseBody {
    items: LiveBlogPost[]
    nextPost?: number
}

export interface LiveBlogPost {
    id: string // blog id
    post: number // post number
    content: {
        posted: string
        blocks: Block[]
        author: string
    }
}

export interface LiveBlogProps {
    blogId: string
    onEvent: (event: PlayerEvent | LinkClickedEvent) => void
    log: Logger
    adUnitPath: string

    liveBlogSocketUrl: string
    liveBlogApi: string

    enabledQuestions?: boolean
}

const CONNECTION_STATUS_CONNECTING = 0
const CONNECTION_STATUS_OPEN = 1
const CONNECTION_STATUS_CLOSING = 2
const CONNECTION_STATUS_CLOSED = 3

const connectionStatus = {
    [CONNECTION_STATUS_CONNECTING]: 'Connecting',
    [CONNECTION_STATUS_OPEN]: 'Open',
    [CONNECTION_STATUS_CLOSING]: 'Closing',
    [CONNECTION_STATUS_CLOSED]: 'Closed',
}

export const LiveBlog: FC<LiveBlogProps> = ({
    onEvent,
    log,
    adUnitPath,
    blogId,
    liveBlogSocketUrl,
    liveBlogApi,
    enabledQuestions,
}) => {
    // To support just the id, or the full link copied from gazette
    const blogIdWithoutPrefix = blogId.replace('liveblog:', '')

    const options = useMemo(
        () => ({
            filter: (message: WebSocketEventMap['message']) => {
                const newPost = JSON.parse(message.data) as LiveBlogPost
                return newPost.hasOwnProperty('id') && newPost.id === blogIdWithoutPrefix
            },
            shouldReconnect: () => true,
            reconnectAttempts: 10,
            reconnectInterval: 3000,
            retryOnError: true,
        }),
        [blogIdWithoutPrefix]
    )

    const [messageHistory, setMessageHistory] = useState<LiveBlogPost[]>([])
    const [, lastMessage, readyState] = useWebSocket(liveBlogSocketUrl, options)
    const [question, setQuestion] = useState('')
    const [lastUpdated, setLastUpdated] = useState(new Date(Date.now()))
    const [formReady, setFormReady] = useState(true)
    const [blogStatus, setBlogStatus] = useState<'loading' | 'ready'>('loading')
    const [nextPost, setNextPost] = useState<number | undefined>(undefined)

    const handleClickSendMessage = useCallback(
        async function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
            event.preventDefault()
            if (question === '') {
                return
            }

            setFormReady(false)

            const response = await fetch(`${liveBlogApi}/posts/${blogIdWithoutPrefix}/questions`, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain', Accept: 'application/json' },
                body: question,
            })

            if (response.status === 201) {
                setQuestion('')
                setFormReady(true)
            }
        },
        [liveBlogApi, blogIdWithoutPrefix, question]
    )

    useEffect(() => {
        if (lastMessage !== null) {
            const newPost = JSON.parse(lastMessage.data) as LiveBlogPost

            setMessageHistory(prev => {
                const existingIndex = prev.findIndex(post => post.post === newPost.post)

                if (existingIndex !== -1) {
                    return prev.map(post => (post.post === newPost.post ? newPost : post))
                } else {
                    return [newPost, ...prev]
                }
            })
        }
    }, [lastMessage, blogIdWithoutPrefix])

    const handlePostsResponse = useCallback(
        async (response: Response) => {
            const { items, nextPost } = (await response.json()) as LiveBlogPostResponseBody
            setNextPost(nextPost)
            const posts = items.filter(post => post.id === blogIdWithoutPrefix)
            setMessageHistory(prev => [...prev, ...posts])
            setBlogStatus('ready')
        },
        [blogIdWithoutPrefix]
    )

    const pageSize = 20

    useEffect(() => {
        async function fetchData() {
            const response = await fetch(
                `${liveBlogApi}/blog/${blogIdWithoutPrefix}/posts?take=${pageSize}`
            )
            if (response.status !== 200) {
                log.error(response.json(), 'Failed to fetch live blog posts')
                setBlogStatus('ready')
                return
            }
            await handlePostsResponse(response)
        }
        fetchData()
    }, [liveBlogApi, blogIdWithoutPrefix, handlePostsResponse, log])

    const handleLoadMore = async () => {
        if (!nextPost) {
            return
        }

        setBlogStatus('loading')

        const response = await fetch(
            `${liveBlogApi}/blog/${blogIdWithoutPrefix}/posts/before/${nextPost}?take=${pageSize}`
        )

        if (response.status !== 200) {
            log.error(response.json(), 'Failed to fetch live blog posts')
            setBlogStatus('ready')
            return
        }

        await handlePostsResponse(response)
    }

    // Interval update for re-rendering to update relative timestamps
    useInterval(() => {
        readyState === 1 && setLastUpdated(new Date(Date.now()))
    }, 60000)

    return (
        <Fragment>
            <StyledConnectionStatus
                title={`Status: ${
                    connectionStatus[readyState]
                }, Last updated: ${lastUpdated.toUTCString()}`}
            />
            <StyledLiveBlogTimeline>
                {messageHistory.map((post, i) => (
                    <StyledLiveBlogPost key={i}>
                        <StyledLiveBlogTimestamp
                            timestampStyle="relative"
                            time={post.content.posted}
                        />
                        <StyledLiveBlogAuthor>{post.content.author}</StyledLiveBlogAuthor>
                        <StyledArticleBlockContent
                            blocks={post.content && post.content.blocks}
                            onEvent={onEvent}
                            log={log}
                            adUnitPath={adUnitPath}
                        />
                    </StyledLiveBlogPost>
                ))}

                <StyledLoadMoreContainer>
                    {blogStatus === 'loading' && (
                        <StyledIconSpinner color={colors.thewest.westblue} />
                    )}
                    {nextPost && blogStatus !== 'loading' && (
                        <StyledLoadMoreButton onClick={handleLoadMore}>
                            Load More
                        </StyledLoadMoreButton>
                    )}
                </StyledLoadMoreContainer>
            </StyledLiveBlogTimeline>
            {enabledQuestions && (
                <StyledQuestionForm>
                    <StyledQuestionInput
                        disabled={!formReady}
                        onChange={event => setQuestion(event.target.value)}
                        value={question}
                        placeholder="Have a question about this Live Blog? Ask our reporters here."
                    />
                    <StyledQuestionSubmit disabled={!formReady} onClick={handleClickSendMessage}>
                        {formReady ? 'Submit' : '------'}
                    </StyledQuestionSubmit>
                </StyledQuestionForm>
            )}
        </Fragment>
    )
}

export const sortByPostedDate = (a: LiveBlogPost, b: LiveBlogPost) => {
    const aDate = new Date(a.content.posted)
    const bDate = new Date(b.content.posted)
    return aDate > bDate ? -1 : aDate < bDate ? 1 : 0
}
